import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './Atlas.css';
import logoImage from '../PA.svg';
import { platformsConfig } from '../platformConfig';

const SEARCH_API_URL = 'https://links.szapfs.org';

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.98
  },
  in: {
    opacity: 1,
    y: 0,
    scale: 1
  },
  out: {
    opacity: 0,
    y: -20,
    scale: 1.02
  }
};

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.6
};

const searchBarVariants = {
  initial: {
    opacity: 0,
    y: -30
  },
  in: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2,
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};

const contentVariants = {
  initial: {
    opacity: 0,
    y: 30
  },
  in: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.4,
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};

function Atlas() {
  const { platformType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Pick up search result synchronously so WBT1 has it on first render
  const [searchSelection, setSearchSelection] = useState(() => {
    if (location.state?.searchResult) {
      const result = location.state.searchResult;
      console.log('[Atlas] received searchResult from navigation:', result);
      // Clear navigation state so it doesn't re-trigger
      window.history.replaceState({}, '');
      return { ...result, _ts: Date.now() };
    }
    return null;
  });
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get platform config from platformsConfig
  const platformConfig = platformsConfig.find(p => p.platformType === platformType);
  const AtlasComponent = platformConfig?.component;
  const searchPlaceholder = platformConfig?.searchPlaceholder || 'Search...';

  useEffect(() => {
    // Simulate loading time for smooth transition
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [platformType]);

  // Redirect to 404 or home if platform type doesn't exist
  useEffect(() => {
    if (!platformConfig) {
      navigate('/');
    }
  }, [platformConfig, navigate]);

  // --- Pure DOM search: no React re-renders until you click a result ---
  const destroyDropdown = useCallback(() => {
    if (dropdownRef.current) {
      dropdownRef.current.remove();
      dropdownRef.current = null;
    }
  }, []);

  const createDropdownContainer = useCallback(() => {
    destroyDropdown();
    if (!searchRef.current) return null;
    const rect = searchRef.current.getBoundingClientRect();
    const div = document.createElement('div');
    Object.assign(div.style, {
      position: 'fixed', top: `${rect.bottom + 4}px`, left: `${rect.left}px`,
      width: `${rect.width}px`, background: '#fff', borderRadius: '8px',
      border: '1px solid #e2e8f0',
      maxHeight: '350px', overflowY: 'auto', zIndex: '2147483647',
    });
    document.body.appendChild(div);
    dropdownRef.current = div;
    return div;
  }, [destroyDropdown]);

  const showMessage = useCallback((text) => {
    const div = createDropdownContainer();
    if (!div) return;
    const msg = document.createElement('div');
    Object.assign(msg.style, { padding: '12px 16px', color: '#94a3b8', fontSize: '0.9rem' });
    msg.textContent = text;
    div.appendChild(msg);
  }, [createDropdownContainer]);

  const buildDropdown = useCallback((results) => {
    if (!results.length) { showMessage('No results found'); return; }
    const div = createDropdownContainer();
    if (!div) return;

    results.forEach((result, i) => {
      const row = document.createElement('div');
      Object.assign(row.style, {
        padding: '10px 16px', cursor: 'pointer', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        borderBottom: i < results.length - 1 ? '1px solid #f1f5f9' : 'none',
      });
      row.onmouseenter = () => { row.style.background = '#f8fafc'; };
      row.onmouseleave = () => { row.style.background = '#fff'; };
      row.onclick = () => {
        console.log('[Search] clicked result:', result);
        console.log('[Search] tab:', result.tab, '| section:', result.section, '| value:', result.value);
        setSearchSelection({ ...result, _ts: Date.now() });
        if (searchInputRef.current) searchInputRef.current.value = '';
        destroyDropdown();
      };

      const label = document.createElement('span');
      Object.assign(label.style, { fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' });
      label.textContent = result.label;

      const badge = document.createElement('span');
      Object.assign(badge.style, {
        fontSize: '0.75rem', color: '#94a3b8', background: '#f1f5f9',
        padding: '2px 8px', borderRadius: '4px', textTransform: 'capitalize',
        flexShrink: '0', marginLeft: '12px',
      });
      badge.textContent = result.tab;

      row.appendChild(label);
      row.appendChild(badge);
      div.appendChild(row);
    });
  }, [createDropdownContainer, showMessage, destroyDropdown, setSearchSelection]);

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!query.trim()) {
      destroyDropdown();
      return;
    }

    showMessage('Searching...');

    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${SEARCH_API_URL}/search?q=${encodeURIComponent(query.trim())}`);
        if (!res.ok) throw new Error('Search failed');
        const results = await res.json();
        buildDropdown(results);
      } catch (err) {
        console.error('Search error:', err);
        destroyDropdown();
      }
    }, 300);
  }, [buildDropdown, destroyDropdown, showMessage]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchRef.current && !searchRef.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) {
        destroyDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [destroyDropdown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => destroyDropdown();
  }, [destroyDropdown]);

  if (!platformConfig) {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={platformType}
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
        className="atlas-container"
      >
        {/* Atlas Header with Search */}
        {/* Simplified Navigation */}
        <div className="navigation-system">
        <div className="main-nav-container">
          {/* Logo */}
          <a href="/" className="main-logo">
            <img src={logoImage} alt="PhenomeAtlas" className="main-logo-image" />
          </a>

          <nav className="platform-menu">
            <a href="/wbt" className="platform-link">WBT-Atlas</a>
            <a href="/contact" className="platform-link">Contact</a>

          </nav>

          {/* Search Bar */}
          <div className="integrated-search" ref={searchRef}>
            <div className="search-input-wrapper">
              <svg className="search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M17.5 17.5L12.5 12.5M14.1667 8.33333C14.1667 11.555 11.555 14.1667 8.33333 14.1667C5.11167 14.1667 2.5 11.555 2.5 8.33333C2.5 5.11167 5.11167 2.5 8.33333 2.5C11.555 2.5 14.1667 5.11167 14.1667 8.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                ref={searchInputRef}
                placeholder={searchPlaceholder || "Search genes, diseases, pathways..."}
                className="integrated-search-input"
                onChange={handleSearchChange}
              />
              <button className="search-submit-btn-integrated">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6.5 12L11 7.5L6.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
        {/* Atlas Content */}
        <motion.div
          variants={contentVariants}
          className="atlas-content"
        >
          {isLoading ? (
            <div className="atlas-loading">
              <div className="loading-spinner"></div>
              <p>Loading {platformConfig.shortName}...</p>
            </div>
          ) : AtlasComponent ? (
            <AtlasComponent searchSelection={searchSelection} />
          ) : (
            <div className="atlas-coming-soon">
              <div className="coming-soon-content">
                <h2>Coming Soon</h2>
                <p>{platformConfig.name} is currently under development.</p>
                <button onClick={() => navigate('/')} className="back-home-btn">
                  Back to Home
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default Atlas;
